import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Battery, Thermometer, Droplets, Satellite, Radio, QrCode, LogIn, Lock, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { TrackOticBrand } from '@/components/Brand';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { qrGetPublic, qrPostPublic } from './qrPublicApi';
import { BindScanner } from './BindScanner';
import { BoundPassportsList } from './BoundPassportsList';

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 py-2 border-b last:border-0" style={{ borderColor: '#F3F1EA' }} data-testid={testId}>
    <Icon size={16} className="text-slate-400" /><span className="text-sm text-slate-500 w-28">{label}</span>
    <span className="text-sm font-medium ml-auto">{value ?? '—'}</span>
  </div>
);

const Problem = ({ title, text, code }) => (
  <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: '#FAF9F6' }} data-testid={`device-problem-${code}`}>
    <div>
      <TrackOticBrand size="xl" />
      <h1 className="text-2xl font-bold mt-6">{title}</h1>
      <p className="text-sm text-slate-500 mt-2">{text}</p>
    </div>
  </div>
);

export default function DevicePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useTrackOticAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    const fetchWith = (qs) => qrGetPublic(`/d/${token}${qs}`).then(setD).catch(e => setErr(e));
    if (user && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(p => fetchWith(`?lat=${p.coords.latitude}&lon=${p.coords.longitude}`), () => fetchWith(''), { timeout: 5000, maximumAge: 60000 });
    } else fetchWith('');
  }, [token, user]);
  useEffect(() => { if (!authLoading) load(); }, [load, authLoading, user]);

  if (err?.loginRequired) return <Navigate to={`/d-login?next=${encodeURIComponent(`/d/${token}`)}`} replace />;
  if (err?.status === 404) return <Problem code="404" title="Not a valid Track0tic code" text="This QR code is not recognised." />;
  if (err?.status === 410) return <Problem code="410" title="This label was replaced" text="Scan the new label on the device." />;
  if (err?.status === 429) return <Problem code="429" title="Too many scans" text="Please wait a minute and try again." />;
  if (err) return <Problem code="error" title="Something went wrong" text={err.message} />;
  if (!d) return <div className="min-h-screen" style={{ background: '#FAF9F6' }} />;

  const onDone = () => { setScanning(false); setRefreshKey(k => k + 1); load(); };
  const rotate = async () => {
    if (!window.confirm(`Rotate the QR token of ${d.trk_id}? The current label stops working.`)) return;
    try { await qrPostPublic(d.actions.rotate_token.replace('/api', '')); toast.success('Token rotated — print the new label'); navigate(d.actions.profile); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen pb-10" style={{ background: '#FAF9F6' }} data-testid="device-page">
      <header className="px-4 pt-6 pb-4 text-center">
        <TrackOticBrand size="lg" />
        <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#E7F2EE', color: '#0B6B55' }} data-testid="genuine-badge">
          <ShieldCheck size={14} /> Genuine Track0tic device
        </div>
        <h1 className="text-3xl font-bold font-mono mt-3" data-testid="device-trk-id">{d.trk_id}</h1>
        <p className="text-sm text-slate-500 mt-1">{d.device_type} · {d.owner_tenant || 'Unallocated'} · last seen {d.last_seen || 'never'}</p>
      </header>

      <main className="max-w-md mx-auto px-4 space-y-4">
        {d.home && (
          <div className="rounded-xl p-4 text-sm" style={{ background: '#FFF7E6', color: '#8A6D1F' }} data-testid="device-home-banner">
            <p className="font-semibold">Device is home — {d.home.open_bindings} passport{d.home.open_bindings === 1 ? '' : 's'} still bound</p>
            <p className="text-xs mt-1">Inside depot hub {d.home.hub_trk_id}. Unbind or hand over the passports still on this device.</p>
          </div>
        )}
        {d.activated === false && <p className="rounded-xl bg-amber-50 text-amber-800 text-sm p-4 text-center" data-testid="not-activated">{d.message}</p>}

        {d.login_required && (
          <button data-testid="login-to-bind" onClick={() => navigate(`/d-login?next=${encodeURIComponent(`/d/${token}`)}`)}
            className="w-full rounded-full py-3 font-bold text-white flex items-center justify-center gap-2" style={{ background: '#0B6B55' }}>
            <LogIn size={16} /> Log in to bind
          </button>
        )}
        {d.not_your_device && <p className="rounded-xl bg-slate-100 text-slate-600 text-sm p-4 text-center" data-testid="not-your-device">Not your device — only {d.owner_tenant} can bind passports here.</p>}

        {d.layer >= 2 && (
          <>
            <section className="rounded-xl bg-white border px-4" style={{ borderColor: '#E5E3DC' }} data-testid="device-stats">
              <Row icon={Battery} label="Battery" value={d.battery_pct != null ? `${d.battery_pct}%` : null} testId="row-battery" />
              <Row icon={Radio} label="Signal" value={d.signal} testId="row-signal" />
              {d.lockbox ? (
                <>
                  <Row icon={Lock} label="Lid" value={d.lockbox.lid_state ? `${d.lockbox.lid_state}${d.lockbox.lid_at ? ` · ${new Date(d.lockbox.lid_at).toLocaleTimeString()}` : ''}` : null} testId="row-lid" />
                  <Row icon={Scale} label="Weight" value={d.lockbox.weight_kg != null ? `${d.lockbox.weight_kg} kg` : null} testId="row-weight" />
                </>
              ) : (
                <>
                  <Row icon={Thermometer} label="Temperature" value={d.temperature_c != null ? `${d.temperature_c} °C` : null} testId="row-temp" />
                  <Row icon={Droplets} label="Humidity" value={d.humidity_pct != null ? `${d.humidity_pct} %` : null} testId="row-humidity" />
                </>
              )}
              {d.gps && <Row icon={Satellite} label="GPS fix" value={d.gps.lat ? `${d.gps.fix_age}` : 'no fix'} testId="row-gps" />}
            </section>
            {d.can_bind && (
              <button data-testid="bind-button" onClick={() => setScanning(true)}
                className="w-full rounded-full py-3 font-bold text-white flex items-center justify-center gap-2" style={{ background: '#0B6B55' }}>
                <QrCode size={18} /> Bind passports
              </button>
            )}
            <BoundPassportsList token={token} refreshKey={refreshKey} />
          </>
        )}

        {d.layer === 3 && (
          <section className="rounded-xl bg-white border p-4 text-sm space-y-2" style={{ borderColor: '#E5E3DC' }} data-testid="admin-layer">
            <h2 className="font-semibold">Track0tic admin</h2>
            <p>Firmware <span className="font-mono">{d.firmware || '—'}</span> · Cert {d.cert_status} · {d.lifecycle_status} · SIM {d.sim_whitelist_status}</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="admin-rotate" onClick={rotate} className="px-3 py-1.5 rounded border text-xs" style={{ borderColor: '#D9D6CD' }}>Rotate token</button>
              <button data-testid="admin-reallocate" onClick={() => navigate(d.actions.profile)} className="px-3 py-1.5 rounded border text-xs" style={{ borderColor: '#D9D6CD' }}>Reallocate</button>
              <button data-testid="admin-retire" onClick={() => navigate(d.actions.profile)} className="px-3 py-1.5 rounded border text-xs" style={{ borderColor: '#D9D6CD' }}>Retire</button>
            </div>
            <details><summary className="cursor-pointer text-slate-500">Provisioning & allocation history ({d.events.length})</summary>
              <ul className="mt-2 space-y-1 text-xs font-mono">{d.events.map((e, i) => <li key={`${e.at}-${i}`}>{e.at?.slice(0, 16)} {e.type}</li>)}</ul></details>
            <details><summary className="cursor-pointer text-slate-500">Fault log ({d.fault_log.length})</summary>
              <ul className="mt-2 space-y-1 text-xs font-mono">{d.fault_log.map((e, i) => <li key={`${e.at}-${i}`}>{e.at?.slice(0, 16)} {e.type}</li>)}</ul></details>
            {d.token_rotations.length > 0 && <p className="text-xs text-slate-400">Token rotated {d.token_rotations.length}×</p>}
          </section>
        )}
      </main>

      {scanning && <BindScanner deviceToken={token} trkId={d.trk_id} onDone={onDone} />}
    </div>
  );
}
